<template>
    <div>

        <fieldset>
            <legend>走刀方向</legend>
            <div class="param-row">
                <label>轴向:</label>
                <select v-model="axis" style="width:100px" @change="onAxisChange">
                    <option value="x">X轴</option>
                    <option value="y">Y轴</option>
                </select>
            </div>
            <div class="param-row">
                <label>方向:</label>
                <select v-model="direction" style="width:200px">
                    <option v-for="opt in dirOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
                <span class="hint">进给方向，避免逆铣</span>
            </div>
        </fieldset>

        <fieldset>
            <legend>加工参数</legend>
            <div class="param-row">
                <label>距离:</label>
                <NumberInput v-model="distance" style="width:100px" />
                <span class="hint">mm（走刀长度）</span>
            </div>
            <div class="param-row">
                <label>深度:</label>
                <NumberInput v-model="depth" style="width:100px" />
                <span class="hint">mm（向下加工总深度，正值）</span>
            </div>
            <div class="param-row">
                <label>每层下刀:</label>
                <NumberInput v-model="levelDepth" style="width:100px" />
                <span class="hint">mm</span>
            </div>
            <div class="param-row">
                <label>安全距离:</label>
                <NumberInput v-model="safeDistance" style="width:100px" />
                <span class="hint">mm（层间侧向退刀）</span>
            </div>
            <div class="param-row">
                <label>主轴转速:</label>
                <NumberInput v-model="spindleRPM" style="width:100px" />
                <span class="hint">RPM</span>
            </div>
            <div class="param-row">
                <label>进给速度:</label>
                <NumberInput v-model="feed" style="width:100px" />
                <span class="hint">mm/min</span>
            </div>
        </fieldset>

        <fieldset>
            <legend>生成</legend>
            <button @click="generateNC">后处理NC文件</button>
        </fieldset>

    </div>
</template>

<script>
/*eslint no-constant-condition: ["error", { "checkLoops": false }]*/

import { ref, computed, inject } from "vue";
import NumberInput from "./NumberInput.vue";

export default {
    components: { NumberInput },
    setup() {
        const fileManager = inject("fileManager");

        // 方向
        const axis = ref("x");           // "x" | "y"
        const direction = ref("left");   // axis=x: "right"|"left"; axis=y: "forward"|"backward"

        // 加工参数
        const distance = ref(100);
        const depth = ref(5);
        const levelDepth = ref(0.5);
        const safeDistance = ref(5);
        const spindleRPM = ref(24000);
        const feed = ref(2000);

        // 根据轴向动态切换方向选项
        const dirOptions = computed(() => {
            if (axis.value === "x") {
                return [
                    { value: "right", label: "向右 (+X) — 铣正后面" },
                    { value: "left", label: "向左 (−X) — 铣正前面" },
                ];
            } else {
                return [
                    { value: "forward", label: "向前 (+Y) — 铣左侧面" },
                    { value: "backward", label: "向后 (−Y) — 铣右侧面" },
                ];
            }
        });

        // 轴向切换时重置方向
        function onAxisChange() {
            if (axis.value === "x") {
                direction.value = "left";
            } else {
                direction.value = "forward";
            }
        }

        return {
            fileManager,
            axis, direction,
            distance, depth, levelDepth, safeDistance, spindleRPM, feed,
            dirOptions,
            onAxisChange,
        };
    },

    methods: {
        generateNC() {
            // 输入验证
            if (this.distance <= 0) {
                alert("距离必须大于0");
                return;
            }
            if (this.depth <= 0) {
                alert("深度必须大于0");
                return;
            }
            if (this.levelDepth <= 0) {
                alert("每层下刀深度必须大于0");
                return;
            }

            // 走刀方向和左侧退刀方向
            // 左侧 = 进给方向逆时针旋转90°
            let feedAxis, leftAxis, feedSign, leftSign;
            if (this.axis === "x") {
                feedAxis = "X";
                leftAxis = "Y";
                feedSign = this.direction === "right" ? 1 : -1;
                leftSign = this.direction === "right" ? 1 : -1;   // 向右→左侧+Y, 向左→左侧-Y
            } else {
                feedAxis = "Y";
                leftAxis = "X";
                feedSign = this.direction === "forward" ? 1 : -1;
                leftSign = this.direction === "forward" ? -1 : 1;  // 向前→左侧-X, 向后→左侧+X
            }

            const dirMap = { right: "右", left: "左", forward: "前", backward: "后" };
            const dirLabel = dirMap[this.direction] || this.direction;
            const steps = Math.ceil(this.depth / this.levelDepth);
            const passes = steps + 1;  // +1 为第一层（当前Z，不下刀）

            // 生成时间
            const now = new Date();
            const ts = now.getFullYear() + "-"
                + String(now.getMonth() + 1).padStart(2, "0") + "-"
                + String(now.getDate()).padStart(2, "0") + " "
                + String(now.getHours()).padStart(2, "0") + ":"
                + String(now.getMinutes()).padStart(2, "0") + ":"
                + String(now.getSeconds()).padStart(2, "0");

            // NC 头部注释
            let nc = `(侧面铣 — ${this.axis.toUpperCase()}轴${dirLabel}  生成时间: ${ts})\n`;
            nc += `(走刀距离=${this.distance.toFixed(2)}mm  总深度=${this.depth.toFixed(2)}mm  每层下刀=${this.levelDepth.toFixed(2)}mm)\n`;
            nc += `(安全退刀=${this.safeDistance.toFixed(2)}mm  主轴=${this.spindleRPM}RPM  进给=${this.feed}mm/min)\n`;
            nc += `(加工层数=${passes} [第1层为当前Z平面]  使用G91增量模式  起点即为刀具当前位置)\n`;
            nc += `M03 S${this.spindleRPM}\n`;
            nc += "G4 P1000\n";
            nc += "G91\n";

            let remaining = this.depth;

            for (let p = 0; p < passes; p++) {
                if (p === 0) {
                    // 第一层：从当前位置直接走刀
                    nc += `G1 ${feedAxis}${(feedSign * this.distance).toFixed(3)} F${this.feed}\n`;
                } else {
                    const dz = Math.min(remaining, this.levelDepth);
                    // 在安全偏移位置下刀
                    nc += `G1 Z${(-dz).toFixed(3)} F${this.feed}\n`;
                    // 进入切削面
                    nc += `G1 ${leftAxis}${(-leftSign * this.safeDistance).toFixed(3)}\n`;
                    // 走刀
                    nc += `G1 ${feedAxis}${(feedSign * this.distance).toFixed(3)}\n`;
                    remaining -= dz;
                }

                // 退刀 + 快速回起点
                nc += `G1 ${leftAxis}${(leftSign * this.safeDistance).toFixed(3)}\n`;
                nc += `G0 ${feedAxis}${(-feedSign * this.distance).toFixed(3)}\n`;
            }

            // 先抬Z回起点，再平移回切削位
            nc += `G0 Z${this.depth.toFixed(3)}\n`;
            nc += `G1 ${leftAxis}${(-leftSign * this.safeDistance).toFixed(3)}\n`;

            // 关主轴
            nc += "M05\n";

            // 输出
            const filename = `侧铣-${this.axis.toUpperCase()}轴${dirLabel}-${this.distance.toFixed(1)}mm-深${this.depth.toFixed(1)}.nc`;
            this.fileManager.addFileTab(filename, nc);
        },
    },
};
</script>
